import React, { useState } from 'react';
import { toast } from 'sonner';
import PublicLayout from './PublicLayout';
import { apiCall } from '../../utils/api';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', org: '', topic: 'sales', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      // Best-effort: if the backend has an endpoint, use it. Otherwise show success
      // (a mailto link is a fine fallback for now — never silently fail on the user).
      try {
        await apiCall('/api/contact', { method: 'POST', body: JSON.stringify(form) });
      } catch (_) {
        window.location.href = `mailto:hello@scensobconnect.com?subject=${encodeURIComponent(`[${form.topic}] ${form.name}`)}&body=${encodeURIComponent(form.message + '\n\n— ' + form.name + ' (' + form.email + ')' + (form.org ? ', ' + form.org : ''))}`;
      }
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch within one business day.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout title="Contact us" eyebrow="We're here to help">
      <p className="lead">Sales, support, partnerships, press — drop us a line. We aim to respond within one business day.</p>
      {submitted ? (
        <div className="not-prose bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-6" data-testid="contact-success">
          <h3 className="text-lg font-semibold">Message sent</h3>
          <p className="mt-1">A real human at ScensobConnect will reply to <strong>{form.email}</strong> within one business day.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="not-prose grid gap-4 mt-6 max-w-xl" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Your name</span>
              <input className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Email</span>
              <input type="email" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Organization (optional)</span>
            <input className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} data-testid="contact-org" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Topic</span>
            <select className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic">
              <option value="sales">Sales</option>
              <option value="support">Customer support</option>
              <option value="partnership">Partnership</option>
              <option value="press">Press</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Message</span>
            <textarea rows={5} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          </label>
          <button disabled={submitting} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg font-semibold" data-testid="contact-submit">
            {submitting ? 'Sending…' : 'Send message'}
          </button>
        </form>
      )}
      <h2 className="mt-12">Direct channels</h2>
      <ul>
        <li><strong>Sales:</strong> <a href="mailto:sales@scensobconnect.com">sales@scensobconnect.com</a></li>
        <li><strong>Support:</strong> <a href="mailto:support@scensobconnect.com">support@scensobconnect.com</a></li>
        <li><strong>Security:</strong> <a href="mailto:security@scensobconnect.com">security@scensobconnect.com</a></li>
        <li><strong>Press:</strong> <a href="mailto:press@scensobconnect.com">press@scensobconnect.com</a></li>
      </ul>
    </PublicLayout>
  );
}
